/**
 * Save/submit and manager-review handlers.
 * handleSaveBid and handleSubmitForReview remain in Full (large, many deps).
 * This hook provides: handleStatusChange, handleRequestModification, handleApprove,
 * handleModificationSuccess, handleApproveSuccess.
 */
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bidAnalysisServices } from 'services/bidAnalysis/bidAnalysisServices';

export type SaveAndSubmitStateBag = {
  setSelectedStatus: (v: string) => void;
  setIsRequestModificationModalVisible: (v: boolean) => void;
  setIsApproveDialogVisible: (v: boolean) => void;
  setBidStatus: (v: string) => void;
};

export function useSaveAndSubmit(
  state: SaveAndSubmitStateBag,
  loadNotes: () => Promise<void>
) {
  const navigate = useNavigate();
  const { bidId } = useParams<{ bidId: string }>();

  const {
    setSelectedStatus,
    setIsRequestModificationModalVisible,
    setIsApproveDialogVisible,
    setBidStatus,
  } = state;

  const handleStatusChange = useCallback((newStatus: string) => {
    setSelectedStatus(newStatus);
  }, [setSelectedStatus]);

  const handleRequestModification = useCallback(() => {
    setIsRequestModificationModalVisible(true);
  }, [setIsRequestModificationModalVisible]);

  const handleApprove = useCallback(() => {
    setIsApproveDialogVisible(true);
  }, [setIsApproveDialogVisible]);

  const handleModificationSuccess = useCallback(async () => {
    if (!bidId) return;
    try {
      const bidDetails = await bidAnalysisServices.getBidDetails(parseInt(bidId));
      if (bidDetails?.status) {
        setBidStatus(bidDetails.status);
        setSelectedStatus(bidDetails.status);
      }
      await loadNotes();
    } catch (error) {
      console.error('Error refreshing bid data:', error);
    }
  }, [bidId, setBidStatus, setSelectedStatus, loadNotes]);

  const handleApproveSuccess = useCallback(async () => {
    if (!bidId) return;
    try {
      const bidDetails = await bidAnalysisServices.getBidDetails(parseInt(bidId));
      if (bidDetails?.status) {
        setBidStatus(bidDetails.status);
        setSelectedStatus(bidDetails.status);
      }
      navigate('/bdportal/bid-analysis/manager-review');
    } catch (error) {
      console.error('Error refreshing bid data:', error);
    }
  }, [bidId, setBidStatus, setSelectedStatus, navigate]);

  return {
    handleStatusChange,
    handleRequestModification,
    handleApprove,
    handleModificationSuccess,
    handleApproveSuccess,
  };
}
